/**
 * ReportSeriesFilters - Filter Bar for Series View
 */

import React from 'react';
import { Search, Filter, X, Calendar, Repeat } from 'lucide-react';
import { useFormat } from '../../../i18n/format.js';
import { CATEGORY_CONFIG } from '../../../hooks/useReportTemplates.js';
import { FREQUENCY_LABELS } from '../../../hooks/useReportSeries.js';

export default function ReportSeriesFilters({
  filters,
  onUpdateFilter,
  onReset,
  stats
}) {
  const { t, fmt } = useFormat();
  const hasActiveFilters = filters.search || filters.category || filters.frequency;

  return (
    <div className="mb-6 space-y-4">
      {/* Search and Filters Row */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => onUpdateFilter('search', e.target.value)}
            placeholder={t('reports.series.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {filters.search && (
            <button
              onClick={() => onUpdateFilter('search', '')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Category Filter */}
        <div className="relative">
          <select
            value={filters.category || ''}
            onChange={(e) => onUpdateFilter('category', e.target.value || null)}
            className="appearance-none pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white cursor-pointer"
          >
            <option value="">{t('reports.series.allCategories')}</option>
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>
                {t(`reports.series.categories.${key}`, { defaultValue: config.label })}
              </option>
            ))}
          </select>
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>

        {/* Frequency Filter */}
        <div className="relative">
          <select
            value={filters.frequency || ''}
            onChange={(e) => onUpdateFilter('frequency', e.target.value || null)}
            className="appearance-none pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white cursor-pointer"
          >
            <option value="">{t('reports.series.allFrequencies')}</option>
            {Object.entries(FREQUENCY_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {t(`reports.schedule.frequencies.${key}.label`, { defaultValue: label })}
              </option>
            ))}
          </select>
          <Repeat className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>

        {/* Reset Button */}
        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
            {t('common.actions.resetFilters')}
          </button>
        )}
      </div>

      {/* Stats Bar */}
      <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
        <div className="flex items-center gap-2">
          {t('reports.series.activeSeries', { count: stats.totalSeries })}
        </div>
        <div className="flex items-center gap-2">
          {t('reports.series.totalSnapshots', { count: stats.totalSnapshots })}
        </div>
        {stats.lastGeneration && (
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {t('reports.series.lastReport')}: {fmt.date(stats.lastGeneration)}
          </div>
        )}
        {Object.keys(stats.byFrequency || {}).length > 0 && (
          <div className="flex items-center gap-2">
            {Object.entries(stats.byFrequency).map(([freq, count]) => (
              <span key={freq} className="px-2 py-0.5 bg-gray-100 rounded-full text-xs">
                {count} {t(`reports.schedule.frequencies.${freq}.label`, { defaultValue: FREQUENCY_LABELS[freq] || freq }).toLowerCase()}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
